import {
  TRAVERSAL_DISCOVERY_SITES,
  TRAVERSAL_GATES,
  TRAVERSAL_INTERACTION_RADIUS_M,
  type TraversalAccessKey,
} from "../../config/traversal.ts";
import {
  TRAVERSAL_STATE_SCHEMA_VERSION,
  type LockedTraversalGate,
  type TraversalDiscoveryResult,
  type TraversalState,
} from "./types.ts";

export function createTraversalState(): TraversalState {
  return {
    schemaVersion: TRAVERSAL_STATE_SCHEMA_VERSION,
    discoveredSiteIds: [],
  };
}

export function discoverTraversalSites(
  state: Readonly<TraversalState>,
  position: Readonly<{ x: number; z: number }>,
): TraversalDiscoveryResult {
  const known = new Set(state.discoveredSiteIds);
  const newlyDiscovered = TRAVERSAL_DISCOVERY_SITES.filter((site) =>
    !known.has(site.id) &&
    Math.hypot(site.position.x - position.x, site.position.z - position.z) <=
      site.radiusM
  );
  if (newlyDiscovered.length === 0) {
    return { state: state as TraversalState, newlyDiscovered: [] };
  }
  return {
    state: {
      ...state,
      discoveredSiteIds: [
        ...state.discoveredSiteIds,
        ...newlyDiscovered.map((site) => site.id),
      ],
    },
    newlyDiscovered,
  };
}

export function nearestLockedTraversalGate(
  access: Readonly<Record<TraversalAccessKey, boolean>>,
  position: Readonly<{ x: number; z: number }>,
): LockedTraversalGate | null {
  return TRAVERSAL_GATES
    .filter((gate) => !access[gate.access])
    .map((gate) => ({
      gate,
      distanceM: Math.hypot(
        gate.position.x - position.x,
        gate.position.z - position.z,
      ),
    }))
    .filter((entry) => entry.distanceM <= TRAVERSAL_INTERACTION_RADIUS_M)
    .sort((left, right) =>
      left.distanceM - right.distanceM ||
      left.gate.id.localeCompare(right.gate.id)
    )[0] ?? null;
}

